import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    forwardRef,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import {ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {debounceTime, distinctUntilChanged, filter, takeUntil} from 'rxjs/operators';
import {Unsubscribable} from '@core/interfaces/unsubscribable';
import {Observable, ReplaySubject} from 'rxjs';
import {FilterFieldOption} from '@core/interfaces/system/system-common';

export interface FilterInfo {
    controlName: string;
    options: FilterFieldOption[];
    silentUpdate?: boolean;
    forceClear?: boolean;
    query?: string;
}

@Component({
    selector: 'ngx-control-panel-checkbox-group',
    styleUrls: ['./checkbox-group.component.scss'],
    template: `
        <nb-form-field>
            <nb-icon nbPrefix icon="search-outline" pack="eva"></nb-icon>
            <input nbInput fieldSize="small" fullWidth [formControl]="filterControl" placeholder="Search..." />
            <button nbSuffix nbButton ghost (click)="filterControl.reset()">
                <nb-icon [icon]="'close'" pack="eva" [attr.aria-label]="'clear search field'"></nb-icon>
            </button>
        </nb-form-field>

        <ngx-icon-box
            *ngIf="selectAllEnabled && !disabled && (displayOptions$ | async)?.length"
            (click)="changeSelection()"
            class="select-all-items"
            [title]="isAllSelected ? 'Unselect All' : 'Select All'"
            [icon]="isAllSelected ? 'square-outline' : 'checkmark-square-2-outline'"
        ></ngx-icon-box>
        <nb-list nbInfiniteList [threshold]="300" (bottomThreshold)="loadNext()">
            <nb-list-item *ngFor="let item of displayOptions$ | async">
                <nb-checkbox
                    [disabled]="disabled"
                    (checkedChange)="selectCheckbox(item, $event)"
                    [checked]="selectedOptions.includes(item)"
                >
                    {{ item.name }}
                </nb-checkbox>
            </nb-list-item>
        </nb-list>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ControlPanelCheckboxGroupComponent),
            multi: true,
        },
    ],
})
export class ControlPanelCheckboxGroupComponent
    extends Unsubscribable
    implements OnInit, OnChanges, ControlValueAccessor
{
    @Input() selectAllEnabled: boolean = true;
    @Input() apiSearch: boolean = false;
    @Input() preSelectedOptions: any[] = [];
    @Input() controlName: string = '';
    @Input() customSelectAll: boolean = false;
    @Input() filterResetTrigger: Observable<boolean>;

    @Output() searchChange: EventEmitter<string> = new EventEmitter<string>();
    @Output() selectAllToggle: EventEmitter<FilterInfo> = new EventEmitter<FilterInfo>();

    selectedOptions: FilterFieldOption[] = [];

    get options() {
        return this._options;
    }

    filteredOptions: FilterFieldOption[] = [];
    displayOptions$: ReplaySubject<{key: string; name: string}[]> = new ReplaySubject(1);

    isAllSelected: boolean = false;

    filterControl: FormControl = new FormControl();
    pageSize: number = 15;
    pageToLoadNext: number = 1;
    loading: boolean = false;
    touched: boolean = false;
    disabled: boolean = false;
    lastQuery: string = '';
    onChange = (value) => {};
    onTouched = () => {};
    private _options: FilterFieldOption[] = [];

    @Input() set options(val: FilterFieldOption[]) {
        this._options = val;
        this.filteredOptions = val;
        this.displayOptions$.next(this.filteredOptions?.slice(0, this.pageSize * this.pageToLoadNext));
    }

    constructor() {
        super();

        this.filterControl.valueChanges
            .pipe(takeUntil(this.unsubscribe$), distinctUntilChanged(), debounceTime(500))
            .subscribe((value: string) => {
                this.filterOptions(value);
            });
    }

    ngOnInit(): void {
        this.filterResetTrigger.pipe(takeUntil(this.unsubscribe$)).subscribe((trigger) => {
            if (trigger) {
                this.filterControl.reset();
            }
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.options?.currentValue || changes.preSelectedOptions?.currentValue) {
            if (this.options?.length && this.preSelectedOptions?.length) {
                this.selectedOptions = [];
                const currentPreselectedFilter = this.preSelectedOptions.find(
                    (option) => option.fieldKey === this.controlName,
                );
                if (currentPreselectedFilter) {
                    this.options.forEach((option) => {
                        if (
                            currentPreselectedFilter.values?.find((opt) => opt === option.key) ||
                            (currentPreselectedFilter.value &&
                                option.key.toLowerCase().includes(currentPreselectedFilter.value.toLowerCase()))
                        ) {
                            this.selectedOptions.push(option);
                        }
                    });
                    this.isAllSelected =
                        this.options.length &&
                        ((currentPreselectedFilter.value && this.selectedOptions.length === this.options.length) ||
                            currentPreselectedFilter.values?.length === this.options.length);
                }
            }
        }
    }

    writeValue(value: any): void {
        this.isAllSelected = this.options?.length && value.length === this.options?.length;
        this.selectedOptions = value;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    markAsTouched(): void {
        if (!this.touched) {
            this.onTouched();
            this.touched = true;
        }
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    changeSelection(): void {
        this.markAsTouched();

        if (this.customSelectAll && this.filterControl.value !== this.lastQuery) {
            this.isAllSelected = false;
        }
        this.isAllSelected = !this.isAllSelected;

        if (this.isAllSelected) {
            this.selectedOptions = [...this.options];
        } else {
            this.selectedOptions = [];
        }

        if (this.customSelectAll) {
            this.lastQuery = this.filterControl.value;
            this.selectAllToggle.emit({
                controlName: this.controlName,
                options: this.selectedOptions,
                query: this.filterControl.value,
            });
        } else {
            this.onChange(this.selectedOptions);
        }
    }

    selectCheckbox(checkbox: FilterFieldOption, selected: boolean): void {
        this.markAsTouched();

        if (this.isAllSelected) {
            this.selectAllToggle.emit({
                controlName: this.controlName,
                options: this.selectedOptions,
                silentUpdate: false,
                forceClear: true,
            });
        }

        if (selected) {
            this.selectedOptions.push(checkbox);

            this.selectedOptions.length === this.options.length && (this.isAllSelected = true);
        } else {
            this.selectedOptions = this.selectedOptions.filter((item) => item.key !== checkbox.key);

            this.selectedOptions.length === this.options.length - 1 && (this.isAllSelected = false);
        }

        this.onChange(this.selectedOptions);
    }

    filterOptions(search: string): void {
        if (this.apiSearch) {
            this.searchChange.emit(search);
            return;
        }

        this.pageToLoadNext = 1;

        if (!this.options) {
            return;
        }

        if (!search) {
            this.filteredOptions = this.options;
            this.displayOptions$.next(this.filteredOptions?.slice(0, this.pageSize * this.pageToLoadNext));
            return;
        }

        search = search.toLowerCase();
        this.filteredOptions = this.options.filter((opt) => opt.name.toLowerCase().indexOf(search) > -1);
        this.displayOptions$.next(this.filteredOptions?.slice(0, this.pageSize * this.pageToLoadNext));
    }

    loadNext(): void {
        if (this.loading) {
            return;
        }

        this.loading = true;
        ++this.pageToLoadNext;
        this.displayOptions$.next(this.filteredOptions?.slice(0, this.pageSize * this.pageToLoadNext));
        this.loading = false;
    }
}
